import axios, { AxiosError, AxiosResponse } from "axios";
import { nuclearn_api_url } from "src/App";
import {
  ChunkedUploadResponse,
  ChunkedUploadStatus,
  UploadContext,
  UnifiedUploadOptions,
  MAX_RETRIES,
  RETRY_DELAY,
  RATE_LIMIT_RETRY_DELAY,
  DEFAULT_UPLOAD_CONFIGS,
} from "../types/unifiedUploadTypes";
import { fileUploadMappingService } from "./fileUploadMappingService";
import { apiErrorDetail, isConflictError } from "src/helpers/apiError";
import { CHUNKED_UPLOAD_THRESHOLD } from "src/constants";

interface EnhancedError extends Error {
  isRateLimit?: boolean;
  isConflictError?: boolean;
  retryAfter?: number;
  statusCode?: number;
  // The server's response, so the src/helpers/apiError readers (status,
  // detail, code) work on these errors as they do on a raw AxiosError.
  response?: AxiosResponse;
}

// Get the appropriate endpoint based on context using the original routes
const getUploadEndpoint = (
  context: UploadContext,
  contextId: string,
  relativePath?: string,
): string => {
  let url: string;
  switch (context) {
    case "project":
      url = `${nuclearn_api_url}/project/project-upload-file/${contextId}`;
      break;
    case "dialogue":
      url = `${nuclearn_api_url}/chat/dialogue-upload-file/${contextId}`;
      break;
    case "dataset":
      url = `${nuclearn_api_url}/datasets/${contextId}/upload-file`;
      break;
    case "project-genius":
      url = `${nuclearn_api_url}/datasets/${contextId}/upload-file`;
      break;
    case "temp-tabular":
      url = `${nuclearn_api_url}/datasets/temp-persist-tabular`;
      break;
    case "timeseries-stage":
      // contextId is the time-series source ID. The endpoint takes a
      // single CSV (chunked-friendly), persists it to the temp dir,
      // and returns {upload_id, filename, n_bytes}.
      url = `${nuclearn_api_url}/time-series/sources/${contextId}/upload-csv-stage`;
      break;
    case "af-import":
      // No contextId in the path. The AF import endpoint reassembles the
      // chunks server-side, creates the af_import row, and enqueues the parse —
      // identical to a single POST, just chunked. POST (not in the PUT list).
      url = `${nuclearn_api_url}/products/equipment-ai/pi/imports`;
      break;
    case "project-folder":
      // contextId is the folder ID. POST (not in uploadChunk's PUT list).
      url = `${nuclearn_api_url}/project-folders/${contextId}/files`;
      break;
    default:
      throw new Error(`Unsupported upload context: ${context}`);
  }
  if (relativePath) {
    const separator = url.includes("?") ? "&" : "?";
    url += `${separator}relative_path=${encodeURIComponent(relativePath)}`;
  }
  return url;
};

// Split file into chunks with sequential indices
export const prepareFileChunks = async (
  file: File,
  chunkSize: number = CHUNKED_UPLOAD_THRESHOLD,
): Promise<{
  chunks: Blob[];
  totalChunks: number;
}> => {
  const chunks: Blob[] = [];
  let offset = 0;

  while (offset < file.size) {
    const chunk = file.slice(offset, offset + chunkSize);
    chunks.push(chunk);
    offset += chunkSize;
  }

  return { chunks, totalChunks: chunks.length };
};

// Helper to check if error is a rate limit error (429)
export const isRateLimitError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 429;
};

// Client errors (4xx) are deterministic — resending the identical request
// fails identically — so they must never be retried (matches the global
// react-query policy in src/config/react-query.ts). 408 (request timeout)
// and 429 (rate limit) are the transient exceptions; 429 gets its own
// backoff handling above wherever this is used.
const isNonRetryableClientError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
};

// Extract a user-facing message for a non-retryable client error. A 403 is a
// permission denial and must read like one — retry noise and generic wording
// made authz denials look like server outages.
const clientErrorMessage = (error: unknown, context: UploadContext): string => {
  const axiosError = error as AxiosError<{ message?: string }>;
  const status = axiosError.response?.status;
  if (status === 403) {
    return context === "project-folder"
      ? "You do not have permission to upload to this folder."
      : "You do not have permission to upload to this dataset.";
  }
  return (
    apiErrorDetail(error) ||
    axiosError.response?.data?.message ||
    `Upload failed (HTTP ${status}).`
  );
};

// Helper to get retry delay from error response
export const getRetryAfterDelay = (error: unknown): number => {
  if (!axios.isAxiosError(error) || !error.response?.headers)
    return RATE_LIMIT_RETRY_DELAY;

  const retryAfter = error.response.headers["retry-after"];
  if (!retryAfter) return RATE_LIMIT_RETRY_DELAY;

  // Parse retry-after header (can be seconds or HTTP date)
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds)) {
    return seconds * 1000; // Convert to milliseconds
  }

  // If it's a date string, calculate delay
  const retryDate = new Date(retryAfter);
  if (!isNaN(retryDate.getTime())) {
    return Math.max(0, retryDate.getTime() - Date.now());
  }

  return RATE_LIMIT_RETRY_DELAY;
};

const appendExtraFields = (
  formData: FormData,
  extraFields: Record<string, string> | undefined,
): void => {
  Object.entries(extraFields ?? {}).forEach(([name, value]) =>
    formData.append(name, value),
  );
};

// Upload a single chunk with retry logic
export const uploadChunk = async (
  context: UploadContext,
  contextId: string,
  filename: string,
  chunk: Blob,
  chunkIndex: number,
  totalChunks: number,
  contentType: string,
  uploadId?: string,
  retryCount = 0,
  rateLimitRetryCount = 0,
  relativePath?: string,
  extraFields?: Record<string, string>,
): Promise<{
  data: Record<string, unknown>;
  status: number;
  isComplete: boolean;
  rateLimited?: boolean;
}> => {
  try {
    const formData = new FormData();
    // Extra fields go first: the server reads the last value of a repeated
    // field, so the protocol fields appended below win over a colliding extra.
    appendExtraFields(formData, extraFields);
    formData.append("chunk_index", chunkIndex.toString());
    formData.append("total_chunks", totalChunks.toString());
    formData.append("filename", filename);
    formData.append("chunk_file", chunk);

    // Only include upload_id for chunks after the first one (chunkIndex > 0)
    if (uploadId && chunkIndex > 0) {
      formData.append("upload_id", uploadId);
    }
    let response = null;
    const endpoint = getUploadEndpoint(context, contextId, relativePath);
    // if context is dataset use put
    if (
      context === "dataset" ||
      context === "temp-tabular" ||
      context === "project-genius" ||
      context === "timeseries-stage"
    ) {
      response = await axios.put(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-File-Upload-Content-Type": contentType,
        },
      });
    } else {
      response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-File-Upload-Content-Type": contentType,
        },
      });
    }

    // 202 acknowledges a non-final chunk. The final chunk answers 200, or
    // 201 on routes that create a resource.
    const isComplete = response.status === 200 || response.status === 201;

    return {
      data: response.data,
      status: response.status,
      isComplete,
    };
  } catch (error) {
    // Handle rate limit errors (429) separately with longer delays
    if (isRateLimitError(error)) {
      console.warn(
        `Upload rate limited for chunk ${chunkIndex}. Current uploads exceed limit.`,
      );

      // Get retry delay from server response
      const retryDelay = getRetryAfterDelay(error);

      // Return rate limit status for immediate user feedback
      if (rateLimitRetryCount === 0) {
        // First rate limit - notify user and wait
        console.log(
          `Waiting ${
            retryDelay / 1000
          } seconds before retrying due to rate limit...`,
        );

        await new Promise((resolve) => setTimeout(resolve, retryDelay));

        return uploadChunk(
          context,
          contextId,
          filename,
          chunk,
          chunkIndex,
          totalChunks,
          contentType,
          uploadId,
          retryCount,
          rateLimitRetryCount + 1,
          relativePath,
          extraFields,
        );
      } else if (rateLimitRetryCount < 3) {
        // Additional rate limit retries with exponential backoff
        const backoffDelay = retryDelay * Math.pow(2, rateLimitRetryCount);
        console.log(
          `Still rate limited. Waiting ${
            backoffDelay / 1000
          } seconds before retry ${rateLimitRetryCount + 1}/3...`,
        );

        await new Promise((resolve) => setTimeout(resolve, backoffDelay));

        return uploadChunk(
          context,
          contextId,
          filename,
          chunk,
          chunkIndex,
          totalChunks,
          contentType,
          uploadId,
          retryCount,
          rateLimitRetryCount + 1,
          relativePath,
          extraFields,
        );
      } else {
        // Too many rate limit retries - throw error with helpful message
        const axiosError = error as AxiosError<{ detail?: string }>;
        const errorMessage =
          axiosError.response?.data?.detail ||
          "Too many concurrent uploads. Please wait for other uploads to complete.";
        const enhancedError: EnhancedError = Object.assign(
          new Error(errorMessage),
          {
            isRateLimit: true,
            retryAfter: retryDelay,
            response: axiosError.response,
          } as const,
        );
        throw enhancedError;
      }
    }

    // Handle 409 conflict errors - don't retry
    if (isConflictError(error)) {
      const axiosError = error as AxiosError<{
        detail?: string;
        message?: string;
      }>;
      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        "File upload conflict. The file may already exist or another upload is in progress.";

      console.error(
        `Conflict error (409) for chunk ${chunkIndex}. Not retrying. Error: ${errorMessage}`,
      );

      const enhancedError: EnhancedError = Object.assign(
        new Error(`Conflict Error: ${errorMessage}`),
        {
          isConflictError: true,
          statusCode: 409,
          response: axiosError.response,
        } as const,
      );
      throw enhancedError;
    }

    // Any other 4xx is deterministic (403 authz denial, 404, 413, 422, ...):
    // retrying resends a request the server will refuse identically, which
    // delays the real error and makes a permission denial look like a server
    // problem. Fail immediately with a message that names the actual cause.
    if (isNonRetryableClientError(error)) {
      const status = (error as AxiosError).response?.status;
      const errorMessage = clientErrorMessage(error, context);
      console.error(
        `Client error (${status}) for chunk ${chunkIndex}. Not retrying. Error: ${errorMessage}`,
      );
      const enhancedError: EnhancedError = Object.assign(
        new Error(errorMessage),
        {
          statusCode: status,
          response: (error as AxiosError).response,
        } as const,
      );
      throw enhancedError;
    }

    // Handle other errors with normal retry logic
    if (retryCount < MAX_RETRIES) {
      // Wait before retrying
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
      );
      return uploadChunk(
        context,
        contextId,
        filename,
        chunk,
        chunkIndex,
        totalChunks,
        contentType,
        uploadId,
        retryCount + 1,
        0, // Reset rate limit retry count
        relativePath,
        extraFields,
      );
    }
    throw error;
  }
};

// Get upload status
export const getUploadStatus = async (
  uploadId: string,
): Promise<ChunkedUploadStatus> => {
  try {
    const response = await axios.get<ChunkedUploadStatus>(
      `${nuclearn_api_url}/chunked-upload/${uploadId}/status`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching upload status:", error);
    throw error;
  }
};

// Cancel an upload
export const cancelUpload = async (uploadId: string): Promise<void> => {
  try {
    await axios.delete(`${nuclearn_api_url}/chunked-upload/${uploadId}`);
  } catch (error) {
    console.error("Error cancelling upload:", error);
    throw error;
  }
};

// Upload a single file using regular upload method
export const uploadFileRegular = async (
  context: UploadContext,
  contextId: string,
  file: File,
  rateLimitRetryCount = 0,
  relativePath?: string,
  extraFields?: Record<string, string>,
): Promise<unknown> => {
  try {
    const formData = new FormData();
    appendExtraFields(formData, extraFields);
    formData.append("files_to_upload", file);

    const response = await axios.post(
      getUploadEndpoint(context, contextId, relativePath),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error) {
    // Handle rate limit errors for regular uploads
    if (isRateLimitError(error)) {
      const retryDelay = getRetryAfterDelay(error);

      if (rateLimitRetryCount < 3) {
        const backoffDelay =
          rateLimitRetryCount === 0
            ? retryDelay
            : retryDelay * Math.pow(2, rateLimitRetryCount);
        console.log(
          `Upload rate limited. Waiting ${
            backoffDelay / 1000
          } seconds before retry ${rateLimitRetryCount + 1}/3...`,
        );

        await new Promise((resolve) => setTimeout(resolve, backoffDelay));

        return uploadFileRegular(
          context,
          contextId,
          file,
          rateLimitRetryCount + 1,
          relativePath,
          extraFields,
        );
      } else {
        const axiosError = error as AxiosError<{ detail?: string }>;
        const errorMessage =
          axiosError.response?.data?.detail ||
          "Too many concurrent uploads. Please wait for other uploads to complete.";
        const enhancedError: EnhancedError = Object.assign(
          new Error(errorMessage),
          {
            isRateLimit: true,
            retryAfter: retryDelay,
            response: axiosError.response,
          } as const,
        );
        throw enhancedError;
      }
    }

    // Handle 409 conflict errors for regular uploads - don't retry
    if (isConflictError(error)) {
      const axiosError = error as AxiosError<{
        detail?: string;
        message?: string;
      }>;
      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        "File upload conflict. The file may already exist or another upload is in progress.";

      console.error(
        `Conflict error (409) in regular upload. Not retrying. Error: ${errorMessage}`,
      );

      const enhancedError: EnhancedError = Object.assign(
        new Error(`Conflict Error: ${errorMessage}`),
        {
          isConflictError: true,
          statusCode: 409,
          response: axiosError.response,
        } as const,
      );
      throw enhancedError;
    }

    // Deterministic client errors surface with a cause-naming message instead
    // of axios's generic "Request failed with status code NNN" (regular
    // uploads never retried these, but the wording still hid 403 denials).
    if (isNonRetryableClientError(error)) {
      const status = (error as AxiosError).response?.status;
      const errorMessage = clientErrorMessage(error, context);
      console.error(
        `Client error (${status}) in regular upload. Error: ${errorMessage}`,
      );
      const enhancedError: EnhancedError = Object.assign(
        new Error(errorMessage),
        {
          statusCode: status,
          response: (error as AxiosError).response,
        } as const,
      );
      throw enhancedError;
    }

    console.error("Error in regular upload:", error);
    throw error;
  }
};

// Main unified upload function - automatically chooses between regular and chunked upload
export const uploadFileUnified = async ({
  context,
  contextId,
  file,
  relativePath,
  onProgress,
  onChunkProgress,
  onTotalChunks,
  onUploadIdReceived,
  onComplete,
  onError,
  chunkSize = CHUNKED_UPLOAD_THRESHOLD,
  extraFields,
}: UnifiedUploadOptions): Promise<ChunkedUploadResponse | unknown> => {
  try {
    // For now, always use chunked upload for dataset context to ensure proper concurrent limiting
    const forceChunked = context === "dataset";
    const useChunked =
      forceChunked || shouldUseChunkedUpload(file, CHUNKED_UPLOAD_THRESHOLD);

    console.log(
      `Upload method for "${file.name}": ${
        useChunked ? "CHUNKED" : "REGULAR"
      } (context: ${context}, forced: ${forceChunked})`,
    );

    if (useChunked) {
      // Use chunked upload for large files
      return await uploadFileInChunks({
        context,
        contextId,
        file,
        relativePath,
        onProgress,
        onChunkProgress,
        onTotalChunks,
        onUploadIdReceived,
        onComplete,
        onError,
        chunkSize,
        extraFields,
      });
    } else {
      // Use regular upload for small files
      onProgress?.(0);

      const result = await uploadFileRegular(
        context,
        contextId,
        file,
        0,
        relativePath,
        extraFields,
      );

      onProgress?.(100);
      onComplete?.(result);

      return {
        status: "completed",
        upload_id: "", // Regular uploads don't have upload IDs
        chunks_received: 1,
        total_chunks: 1,
        progress: 1,
        result: result,
      };
    }
  } catch (error) {
    console.error("Error in unified upload:", error);

    // Provide more context for different error types
    let errorMessage = error instanceof Error ? error.message : "Upload failed";
    const enhanced = error as EnhancedError;
    if (enhanced?.isRateLimit) {
      errorMessage = `Upload limit reached: ${errorMessage}. Please try again later.`;
    } else if (enhanced?.isConflictError) {
      errorMessage = `Upload Conflict: The file already exists or another upload is in progress. Please try again or use a different filename.`;
    }

    onError?.(errorMessage);
    throw error;
  }
};

// Legacy function for backwards compatibility - now uses unified approach
export const uploadFileInChunks = async ({
  context,
  contextId,
  file,
  relativePath,
  onProgress,
  onChunkProgress,
  onTotalChunks,
  onUploadIdReceived,
  onComplete,
  onError,
  chunkSize = CHUNKED_UPLOAD_THRESHOLD,
  extraFields,
}: UnifiedUploadOptions): Promise<ChunkedUploadResponse> => {
  try {
    // Prepare file chunks
    const { chunks, totalChunks } = await prepareFileChunks(file, chunkSize);

    // Notify about total chunks
    onTotalChunks?.(totalChunks);

    let uploadId: string | undefined;
    let finalResult: unknown;

    // Handle single chunk uploads (immediate completion)
    if (totalChunks === 1) {
      const response = await uploadChunk(
        context,
        contextId,
        file.name,
        chunks[0],
        0,
        totalChunks,
        file.type,
        undefined,
        0,
        0,
        relativePath,
        extraFields,
      );

      if (response.isComplete) {
        // Single chunk completed immediately
        finalResult = response.data;
        onProgress?.(100);
        onChunkProgress?.(0, true);
        onComplete?.(finalResult);

        return {
          status: "completed",
          upload_id: "", // Single chunk uploads don't have upload IDs
          chunks_received: 1,
          total_chunks: 1,
          progress: 1,
          result: finalResult,
        };
      } else {
        throw new Error("Unexpected response for single chunk upload");
      }
    }

    // Handle multi-chunk uploads
    for (let i = 0; i < chunks.length; i++) {
      try {
        const response = await uploadChunk(
          context,
          contextId,
          file.name,
          chunks[i],
          i,
          totalChunks,
          file.type,
          uploadId,
          0,
          0,
          relativePath,
          extraFields,
        );

        console.log(
          `Chunk ${i} response status: ${response.status}, data:`,
          response.data,
        );

        if (i === 0) {
          // First chunk - expect 202 with upload_id
          if (response.status === 202) {
            // The upload_id might be wrapped in a detail object
            const chunkData = response.data as {
              detail?: { upload_id?: string };
              upload_id?: string;
            };
            uploadId = chunkData.detail?.upload_id || chunkData.upload_id;

            console.log("First chunk 202 response:", response.data);
            console.log("Extracted upload_id:", uploadId);

            if (uploadId) {
              onUploadIdReceived?.(uploadId);

              // Add mapping to the mapping service
              fileUploadMappingService.addMapping(
                file.name,
                uploadId,
                context === "dialogue" ? parseInt(contextId) : undefined,
                context,
              );

              console.log(
                `Successfully stored upload_id: ${uploadId} for file: ${file.name}`,
              );
              console.log(
                `Will use upload_id: ${uploadId} for remaining ${
                  totalChunks - 1
                } chunks`,
              );
            } else {
              console.error("Response data:", response.data);
              throw new Error(
                "Invalid or missing upload_id in first chunk response",
              );
            }
          } else {
            console.error("Unexpected first chunk response:", response);
            throw new Error(
              `Expected 202 status for first chunk, got ${response.status}`,
            );
          }
        } else if (i === chunks.length - 1) {
          // Final chunk - expect 200/201 with the route's result object
          if (response.isComplete) {
            finalResult = response.data;
            console.log(
              `Final chunk completed with result object:`,
              finalResult,
            );
          } else {
            console.error(`Final chunk unexpected response:`, response);
            throw new Error("Final chunk did not complete upload");
          }
        } else {
          // Middle chunks - expect 202 status
          if (response.status !== 202) {
            console.warn(
              `Middle chunk ${i} got unexpected status ${response.status}, expected 202`,
            );
          }
        }

        // Calculate progress
        const progress = ((i + 1) / totalChunks) * 100;
        console.log(
          `Chunk ${i}/${
            totalChunks - 1
          } uploaded successfully. Progress: ${progress.toFixed(1)}%`,
        );

        onProgress?.(progress);
        onChunkProgress?.(i, true);
      } catch (error) {
        console.error(`Error uploading chunk ${i}/${totalChunks - 1}:`, error);

        // Provide more detailed error message for different error types
        let errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        const enhanced = error as EnhancedError;
        if (enhanced?.isRateLimit) {
          errorMessage = `Server is busy: ${errorMessage}`;
          console.log(
            `⏳ Upload rate limited. Server has reached maximum concurrent uploads.`,
          );
        } else if (enhanced?.isConflictError) {
          errorMessage = `Conflict error: The file already exists or another upload is in progress.`;
          console.log(`Conflict error (409) detected. Upload cannot continue.`);
        }

        onChunkProgress?.(i, false, errorMessage);

        // If we have an upload_id, try to cancel the upload
        if (uploadId) {
          try {
            console.log(`Cancelling upload ${uploadId} due to error`);
            await cancelUpload(uploadId);
          } catch (cancelError) {
            console.error("Error cancelling upload:", cancelError);
          }
        }

        onError?.(errorMessage);
        throw error;
      }
    }

    console.log(
      `All ${totalChunks} chunks uploaded successfully for ${file.name}`,
    );

    if (!finalResult) {
      throw new Error("No final result received from upload");
    }

    onComplete?.(finalResult);

    return {
      status: "completed",
      upload_id: uploadId || "",
      chunks_received: totalChunks,
      total_chunks: totalChunks,
      progress: 1,
      result: finalResult,
    };
  } catch (error) {
    console.error("Error in chunked upload:", error);
    onError?.(error instanceof Error ? error.message : "Upload failed");
    throw error;
  }
};

// Utility function to check if file should use chunked upload
export const shouldUseChunkedUpload = (
  file: File,
  threshold?: number,
): boolean => {
  // Use the configured threshold from constants, fallback to provided threshold or 10MB
  const actualThreshold =
    threshold || CHUNKED_UPLOAD_THRESHOLD || 10 * 1024 * 1024;
  const useChunked = file.size > actualThreshold;
  console.log(
    `File "${file.name}" size: ${(file.size / 1024 / 1024).toFixed(
      2,
    )}MB, threshold: ${(actualThreshold / 1024 / 1024).toFixed(
      2,
    )}MB, use chunked: ${useChunked}`,
  );
  return useChunked;
};

// Validate file based on context using configuration
export const validateFileForContext = (
  file: File,
  context: UploadContext,
): { isValid: boolean; error?: string } => {
  // Get configuration for this context
  const config = DEFAULT_UPLOAD_CONFIGS[context];

  // If no config found, allow all files (fail open)
  if (!config) {
    console.warn(`No upload configuration found for context: ${context}`);
    return { isValid: true };
  }

  // If no extension restrictions defined, allow all files
  if (!config.allowedExtensions || config.allowedExtensions.length === 0) {
    return { isValid: true };
  }

  // Get file extension
  const extension = "." + file.name.split(".").pop()?.toLowerCase();

  // Check if extension is allowed
  if (!config.allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: `Invalid file type for ${context}. Supported types: ${config.allowedExtensions.join(
        ", ",
      )}`,
    };
  }

  // Check file size if configured
  if (config.maxFileSize && file.size > config.maxFileSize) {
    const maxSizeInMB = (config.maxFileSize / (1024 * 1024)).toFixed(0);
    const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      isValid: false,
      error: `File too large (${fileSizeInMB} MB). Maximum allowed size for ${context}: ${maxSizeInMB} MB`,
    };
  }

  return { isValid: true };
};

// Calculate optimal chunk size based on file size
export const calculateOptimalChunkSize = (fileSize: number): number => {
  if (fileSize < 10 * 1024 * 1024) {
    // < 10MB
    return 1 * 1024 * 1024; // 1MB chunks
  } else if (fileSize < 100 * 1024 * 1024) {
    // < 100MB
    return 5 * 1024 * 1024; // 5MB chunks
  } else if (fileSize < 1024 * 1024 * 1024) {
    // < 1GB
    return 10 * 1024 * 1024; // 10MB chunks
  } else {
    return 25 * 1024 * 1024; // 25MB chunks
  }
};
